/**
 * Universal Explainer page used before each practice.
 * Pass props: `title`, `lines` (array of paragraph strings), `cta`, `to`.
 * Same navy ritual aesthetic as PracticesHome.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PracticeExplainer({ title, eyebrow, lines, cta, to, accent = "rgba(217,180,120,0.55)", testId = "practice-explainer" }) {
  const navigate = useNavigate();
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setArrived(true), 80);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className="min-h-screen relative overflow-hidden"
      data-testid={testId}
      style={{
        background:
          "radial-gradient(ellipse at 50% 18%, #0E1A2C 0%, #0A1424 32%, #060B16 68%, #03060D 100%)",
      }}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0"
           style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(217,165,90,0.05) 0%, rgba(217,165,90,0.015) 40%, transparent 75%)" }} />
      <motion.div aria-hidden
        animate={{ opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 35%, rgba(160,180,220,0.07), transparent 60%)" }}
      />
      <header className="relative z-10 px-5 pt-5 pb-2 flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-lg hover:bg-white/[0.04] transition-colors"
          aria-label="Back"
          data-testid={`${testId}-back`}
        >
          <ArrowLeft size={20} className="text-white/55" />
        </button>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-7 pt-12 pb-20">
        {eyebrow && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
            transition={{ duration: 0.9 }}
            className="text-white/40 text-[10px] tracking-[0.32em] uppercase text-center mb-5"
          >
            {eyebrow}
          </motion.p>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 14 }}
          transition={{ duration: 1.1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-white/95 text-4xl sm:text-5xl tracking-tight text-center"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
        >
          {title}
        </motion.h1>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: arrived ? 0.55 : 0 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          aria-hidden
          className="mx-auto mt-7 h-px w-24"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="mt-9 space-y-6">
          {lines.map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 10 }}
              transition={{ duration: 1.1, delay: 0.55 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="text-white/65 text-base leading-relaxed text-center"
              style={{ fontWeight: 300, fontSize: "16.5px", lineHeight: 1.65 }}
            >
              {line}
            </motion.p>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 14 }}
          transition={{ duration: 1.1, delay: 0.55 + lines.length * 0.18 + 0.3 }}
          className="mt-14 flex justify-center"
        >
          <button
            onClick={() => navigate(to)}
            data-testid={`${testId}-cta`}
            className="relative px-10 py-3.5 rounded-full text-white/90 text-sm tracking-[0.2em] uppercase transition-transform active:scale-[0.97]"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
              border: `1px solid ${accent}`,
              boxShadow: `0 12px 40px -16px ${accent}, inset 0 1px 0 rgba(255,255,255,0.05)`,
              backdropFilter: "blur(8px)",
            }}
          >
            {cta}
          </button>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: arrived ? 1 : 0 }}
          transition={{ duration: 1.4, delay: 1.4 }}
          className="text-center text-white/25 text-[11px] tracking-[0.22em] uppercase mt-10"
        >
          Keep it simple.
        </motion.p>
      </main>
    </div>
  );
}
